import { useMemo } from 'react';

import { DateRangePicker, Input, Select, Text, type TypeSelectOption } from '@thefarmersfront/kitchen';

import clsx from 'clsx';

import { SearchFilter } from '~/components/common';

import { getAllTemperatureOptions } from '~/utils';

import { OPTION_ALL } from '~/constants';

import {
  TypeReceiveFilterOptions,
  TypeReceiveRequestsFilter,
  TypeReceiveRequestsFilterStatus,
} from '~/types/receive.types';

import styles from './ReceiveRequestSearchFilter.module.scss';

const ReceiveRequestSearchFilter = ({
  options,
  values,
  valuesState,
  onChange,
  onChangeStatus,
  onSearch,
  onReset,
}: {
  options?: TypeReceiveFilterOptions;
  values: TypeReceiveRequestsFilter;
  valuesState: TypeReceiveRequestsFilterStatus;
  onChange: (values: Partial<TypeReceiveRequestsFilter>) => void;
  onChangeStatus: (values: Partial<TypeReceiveRequestsFilterStatus>) => void;
  onSearch: () => void;
  onReset: () => void;
}) => {
  const optionTemperature = useMemo<TypeSelectOption<string>[]>(() => {
    if (values.center.value === OPTION_ALL.value) {
      return getAllTemperatureOptions(options?.groupedTemperatureByCenter ?? {});
    }
    return options?.groupedTemperatureByCenter[values.center.value] ?? [OPTION_ALL];
  }, [options?.groupedTemperatureByCenter, values.center.value]);

  return (
    <SearchFilter formId="receive-requests-form" onSubmit={onSearch} onReset={onReset}>
      <div className={clsx(styles['filter-container'], styles['filter-keyword'])}>
        <Text size="small" weight="semi-bold" element="p">
          키워드
        </Text>
        <div className={styles['input-container']}>
          <Select
            id="keywordType"
            size="small"
            placeholder="선택"
            options={options?.keywordType ?? []}
            value={values.keywordType}
            onChange={(_: unknown, value: TypeSelectOption<string>) => {
              if (value?.value === values.keywordType?.value) return;
              onChange({ keywordType: value, keyword: '' });
            }}
          />
          <Input
            type="text"
            size="small"
            value={values.keyword}
            placeholder="입력"
            maxLength={200}
            onChange={(e) => onChange({ keyword: e.target.value })}
            onBlur={(e) => onChange({ keyword: e.target.value.trim() })}
          />
        </div>
      </div>
      <div className={clsx(styles['filter-container'], styles['filter-center'])}>
        <Text size="small" weight="semi-bold" element="p">
          입고 센터
        </Text>
        <div className={styles['input-container']}>
          <Select
            id="center"
            size="small"
            placeholder="선택"
            options={options?.center ?? []}
            value={values.center}
            onChange={(_: unknown, value: TypeSelectOption<string>) => {
              if (value?.value === values.center?.value) return;
              onChange({ center: value, temperature: OPTION_ALL });
            }}
          />
          <Select
            id="temperature"
            size="small"
            placeholder="선택"
            disabled={values.center.value === OPTION_ALL.value}
            options={optionTemperature ?? []}
            value={values.temperature}
            onChange={(_: unknown, value: TypeSelectOption<string>) => {
              if (value?.value === values.temperature?.value) return;
              onChange({ temperature: value });
            }}
          />
        </div>
      </div>
      <div className={clsx(styles['filter-container'], styles['filter-date'])}>
        <Text size="small" weight="semi-bold" element="p">
          기간
        </Text>
        <div className={styles['input-container']}>
          <Select
            id="dateType"
            size="small"
            placeholder="선택"
            options={options?.dateType ?? []}
            value={values.dateType}
            onChange={(_: unknown, value: TypeSelectOption<string>) => {
              if (value?.value === values.dateType?.value) return;
              onChange({ dateType: value });
            }}
          />
          <DateRangePicker
            size="small"
            placeholder={{ start: '시작일', end: '종료일' }}
            status={valuesState.dateRange}
            value={values.dateRange}
            onChange={(value) => {
              onChange({ dateRange: value });
              onChangeStatus({ dateRange: 'default' });
            }}
            onFocus={() => onChangeStatus({ dateRange: 'default' })}
          />
        </div>
      </div>
      <div className={clsx(styles['filter-container'], styles['filter-status'])}>
        <Text size="small" weight="semi-bold" element="p">
          요청 상태
        </Text>
        <div className={styles['input-container']}>
          <Select
            id="status"
            size="small"
            placeholder="선택"
            options={options?.status ?? []}
            value={values.status}
            onChange={(_: unknown, value: TypeSelectOption<string>) => {
              if (value?.value === values.status?.value) return;
              onChange({ status: value });
            }}
          />
        </div>
      </div>
      <div className={clsx(styles['filter-container'], styles['filter-release-code'])}>
        <Text size="small" weight="semi-bold" element="p">
          입고 방법
        </Text>
        <div className={styles['input-container']}>
          <Select
            id="releaseCode"
            size="small"
            placeholder="선택"
            options={options?.releaseCode ?? []}
            value={values.releaseCode}
            onChange={(_: unknown, value: TypeSelectOption<string>) => {
              if (value?.value === values.releaseCode?.value) return;
              onChange({ releaseCode: value });
            }}
          />
        </div>
      </div>
      <div className={clsx(styles['filter-container'], styles['filter-x-dock'])}>
        <Text size="small" weight="semi-bold" element="p">
          입고대행서비스
        </Text>
        <div className={styles['input-container']}>
          <Select
            id="xDock"
            size="small"
            placeholder="선택"
            options={options?.xDock ?? []}
            value={values.xDock}
            onChange={(_: unknown, value: TypeSelectOption<string>) => {
              if (value?.value === values.xDock?.value) return;
              onChange({ xDock: value });
            }}
          />
        </div>
      </div>
    </SearchFilter>
  );
};

export default ReceiveRequestSearchFilter;
